import React from "react";
import { Card, Title, Text, Button, Group, Stack, Badge, Table, Loader } from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { RoleGuard } from "./RoleGuard";
import { useMeeting, useAgendaReturnFormsByMeeting } from "../hooks";

interface AgendaReturnFormTabProps {
  meetingId: string;
}

export const AgendaReturnFormTab: React.FC<AgendaReturnFormTabProps> = ({ meetingId }) => {
  const navigate = useNavigate();
  const { data: meeting, isLoading: meetingLoading } = useMeeting(meetingId);
  const { data: forms, isLoading: formsLoading } = useAgendaReturnFormsByMeeting(meetingId);

  const canCreate = meeting != null && meeting.status !== "CANCELLED";

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: "gray",
      PENDING: "yellow",
      APPROVED: "green",
      REJECTED: "red",
    };
    return <Badge color={colors[status] || "gray"}>{status}</Badge>;
  };

  if (meetingLoading || formsLoading) {
    return (
      <Card>
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">Loading agenda return forms...</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Agenda return forms</Title>
          {canCreate && (
            <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() =>
                  navigate(`/bid-committee/agenda-return-forms/new?meetingId=${meetingId}`)
                }
              >
                New return form
              </Button>
            </RoleGuard>
          )}
        </Group>

        {!forms || forms.length === 0 ? (
          <Stack gap="md" align="center" py="xl">
            <Text c="dimmed" size="lg">
              No agenda return forms for this meeting yet.
            </Text>
          </Stack>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Form number</Table.Th>
                <Table.Th>Agenda item</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {forms.map((form) => (
                <Table.Tr key={form.id}>
                  <Table.Td>{form.formNumber ?? "—"}</Table.Td>
                  <Table.Td>{form.agendaItem?.subject ?? "—"}</Table.Td>
                  <Table.Td>{getStatusBadge(form.status)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => navigate(`/bid-committee/agenda-return-forms/${form.id}`)}
                      >
                        View
                      </Button>
                      {form.status === "DRAFT" && (
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={() =>
                            navigate(`/bid-committee/agenda-return-forms/${form.id}/edit`)
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
};
